import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';

import { useAuth } from './AuthContext';
import { rootUrl } from '../../config/config';
import Alerty from '../common/Alerty';

const Transaction = () => {
  // using imported rootUrl
  const { user } = useAuth();

  // drivers state removed as it's not used
  const [carPlates, setCarPlates] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  // selectedBalance state removed as it's not used
  const [selectedActualBalance, setSelectedActualBalance] = useState('');
  const [errors, setErrors] = useState({});
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loadingCarDetails, setLoadingCarDetails] = useState(false);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [transAmountLimits, setTransAmountLimits] = useState({});

  const [newTrans, setNewTrans] = useState({
    car_trans_type: '',
    date: new Date().toISOString().split('T')[0], // Set today as default
    amount: '',
    notes: '',
    car: '',
    bal: '',
    input_by: '',
    your_ref: '',
  });



  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && user.username) {
      setNewTrans((prevTrans) => ({
        ...prevTrans,
        input_by: user.username,
      }));
    }
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('authToken') || 'default-token';
      try {
        const [carPlatesResponse, typeResponse, limitsResponse] = await Promise.all([
          axios.get(`${rootUrl}/cars/plates`, { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${rootUrl}/payments/types/car`, { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${rootUrl}/system/car_trans_amount_limits`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);
        setCarPlates(carPlatesResponse.data);
        const typesData = typeResponse.data.map(item => ({
          id: item.id,
          type: item.type,
          short_desc: item.short_desc,
          long_desc: item.long_desc,
          def: item.def
        }));
        setTypes(typesData);

        // Set default transaction type
        const defaultType = typesData.find(type => type.def === true);
        if (defaultType) {
          setNewTrans(prev => ({
            ...prev,
            car_trans_type: defaultType.type
          }));
        }

        const limitsValue = limitsResponse?.data?.value;
        if (limitsValue) {
          try {
            const parsed = JSON.parse(limitsValue);
            const normalized = Object.entries(parsed || {}).reduce((acc, [key, value]) => {
              const num = parseFloat(value);
              if (!Number.isNaN(num)) {
                acc[key] = num;
              }
              return acc;
            }, {});
            setTransAmountLimits(normalized);
          } catch (err) {
            console.error('Error parsing car_trans_amount_limits:', err);
          }
        }
      } catch (error) {
        console.error("Error fetching data: ", error);
      }
    };

    fetchData();
  }, []);

  const handleAddTransaction = async () => {
    let newErrors = {};

    if (!newTrans.car_trans_type) newErrors.car_trans_type = 'Please fill in transaction type';
    if (!newTrans.car) newErrors.car = 'Please fill in car';
    if (!newTrans.date) newErrors.date = 'Please fill in date';
    if (!newTrans.notes) newErrors.notes = 'Please fill in the notes';
    if (!newTrans.amount) newErrors.amount = 'Please fill in amount';
    if (!newTrans.your_ref) newErrors.your_ref = 'Please fill in ref code';

    const amountValue = parseFloat(newTrans.amount);
    if (!Number.isNaN(amountValue)) {
      const limit = transAmountLimits[newTrans.car_trans_type];
      if (limit !== undefined && limit !== null && amountValue > limit) {
        newErrors.amount = `Amount exceeds maximum of ${limit} for ${newTrans.car_trans_type}`;
      }
    }



    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true)

    try {
      const transData = { ...newTrans, amount: parseFloat(newTrans.amount) };
      const response = await axios.post(`${rootUrl}/cars/${newTrans.car}/trans`, transData);

      if (response.status === 200) {
        const msg = "Transaction added successfully!";
        setSuccessMessage(msg);
        setSuccessOpen(true);
        // auto-clear after 6s
        setTimeout(() => setSuccessOpen(false), 6000);
        setNewTrans({
          car_trans_type: '',
          date: new Date().toISOString().split('T')[0], // Reset to today
          amount: '',
          notes: '',
          car: '',
          bal: '',
          input_by: user.username,
          your_ref: ''
        });
        setSelectedDriver('');
        // removed outdated balance state
        setSelectedActualBalance('');
      }

    } catch (error) {
      console.error("Error adding transaction: ", error);
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.error || error.response.data.message || 'An error occurred while adding the transaction';
        setErrors({ api: errorMessage });
        setErrorOpen(true);

        // If it's a date-related error, also set the date field error
        if (errorMessage.toLowerCase().includes('date') || errorMessage.toLowerCase().includes('week')) {
          setErrors(prev => ({ ...prev, date: errorMessage }));
        }
      } else {
        setErrors({ api: 'Network error. Please check your connection and try again.' });
        setErrorOpen(true);
      }
    } finally {
      setIsSaving(false)
    }
  };

  const handleCarPlateChange = async (e) => {
    const selectedPlate = e.target.value;
    setNewTrans((prevTrans) => ({ ...prevTrans, car: selectedPlate }));

    if (selectedPlate) {
      setLoadingCarDetails(true);
      try {
        const token = localStorage.getItem('authToken') || 'default-token';
        const response = await axios.get(`${rootUrl}/cars/${selectedPlate}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const selectedCar = response.data;
        setSelectedDriver(selectedCar ? selectedCar.driver_name : '');
        // removed outdated balance state
        setSelectedActualBalance(selectedCar ? selectedCar.actual_bal : 'N/A');

        // Set last transaction details
        if (selectedCar) {
          setLastTransaction({
            date: selectedCar.last_trans_date,
            doc_no: selectedCar.last_trans_doc_no,
            type: selectedCar.last_trans_type,
            by: selectedCar.last_trans_by,
            your_ref: selectedCar.last_trans_your_ref
          });
        } else {
          setLastTransaction(null);
        }
      } catch (error) {
        console.error("Error fetching car details:", error);
        setSelectedDriver('');
        // removed outdated balance state
        setSelectedActualBalance('N/A');
        setLastTransaction(null);
      } finally {
        setLoadingCarDetails(false);
      }
    } else {
      setSelectedDriver('');
      // removed outdated balance state
      setSelectedActualBalance('N/A');
      setLastTransaction(null);
    }
  };

  // Helper function to clear API errors when user starts typing

  // Handle paste event in notes field
  const handleNotesPaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    const words = pastedText.trim().split(/\s+/);

    // Extract reference code (first word if 10 characters)
    if (words.length > 0) {
      const firstWord = words[0];
      if (firstWord.length === 10) {
        setNewTrans(prev => ({
          ...prev,
          your_ref: firstWord.toUpperCase()
        }));
      }
    }

    // Extract amount from the message
    const amountMatch = pastedText.match(/Ksh([\d,]+\.?\d*)/);
    if (amountMatch) {
      const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
      if (!isNaN(amount)) {
        setNewTrans(prev => ({
          ...prev,
          amount: amount.toString()
        }));
      }
    }

    // Extract date from the message (format: 22/7/25)
    const dateMatch = pastedText.match(/(\d{1,2})\/(\d{1,2})\/(\d{2})/);
    if (dateMatch) {
      const day = parseInt(dateMatch[1]);
      const month = parseInt(dateMatch[2]);
      const year = parseInt(dateMatch[3]) + 2000; // Convert 2-digit year to 4-digit

      // Create date string in YYYY-MM-DD format
      const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

      setNewTrans(prev => ({
        ...prev,
        date: dateString
      }));
    }
  };

  const isSaveDisabled = !newTrans.car || !newTrans.notes || !newTrans.amount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Post a Transaction</h1>
          <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
        </div>

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">

          {errors.api && <Alerty open={errorOpen} setOpen={setErrorOpen} color="red" message={errors.api} variant="solid" />}
          {successMessage && <Alerty open={successOpen} setOpen={setSuccessOpen} color="green" message={successMessage} variant="solid" />}

          {/* Form Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Transaction Date */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Transaction Date</label>
                <input
                  type="date"
                  value={newTrans.date}
                  onChange={(e) => {
                    setNewTrans({ ...newTrans, date: e.target.value });
                    if (errors.date || errors.api) {
                      setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        delete newErrors.date;
                        delete newErrors.api;
                        return newErrors;
                      });
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white"
                />
                {errors.date && <p className="text-red-500 text-sm">{errors.date}</p>}
              </div>

              {/* Transaction Type */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Transaction Type</label>
                <Select
                  value={
                    newTrans.car_trans_type
                      ? { value: newTrans.car_trans_type, label: types.find((type) => type.type === newTrans.car_trans_type)?.long_desc || '' }
                      : null
                  }
                  onChange={(selectedOption) =>
                    setNewTrans({
                      ...newTrans,
                      car_trans_type: selectedOption ? selectedOption.value : '',
                    })
                  }
                  options={types.map((type) => ({
                    value: type.type,
                    label: type.long_desc,
                  }))}
                  placeholder="Choose transaction type"
                  className="w-full"
                  classNamePrefix="custom-select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      padding: '4px',
                      '&:hover': {
                        borderColor: '#FF5733'
                      }
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff8f5' : 'white',
                      color: state.isSelected ? 'white' : '#2a2e30'
                    })
                  }}
                />
                {errors.car_trans_type && <p className="text-red-500 text-sm">{errors.car_trans_type}</p>}
              </div>

              {/* Car Plate */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Car Plate</label>
                <Select
                  value={
                    newTrans.car
                      ? { value: newTrans.car, label: newTrans.car }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleCarPlateChange({
                      target: { value: selectedOption.value },
                    })
                  }
                  options={carPlates.map((plate) => ({
                    value: plate,
                    label: plate,
                  }))}
                  placeholder="Select car plate"
                  className="w-full"
                  classNamePrefix="custom-select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      padding: '4px',
                      '&:hover': {
                        borderColor: '#FF5733'
                      }
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff8f5' : 'white',
                      color: state.isSelected ? 'white' : '#2a2e30'
                    })
                  }}
                />
                {errors.car && <p className="text-red-500 text-sm">{errors.car}</p>}

                {/* Car Details Link */}
                {newTrans.car && (
                  <div className="mt-2">
                    <a
                      href={`/cars/${newTrans.car}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-[#FF5733] hover:text-[#e64a2b] transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Car Details
                    </a>
                  </div>
                )}
              </div>

              {/* Reference */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Reference Code</label>
                <input
                  type="text"
                  value={newTrans.your_ref}
                  onChange={(e) => {
                    const uppercaseValue = e.target.value.toUpperCase();
                    setNewTrans({ ...newTrans, your_ref: uppercaseValue });
                    if (errors.your_ref) {
                      setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        delete newErrors.your_ref;
                        return newErrors;
                      });
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white"
                  placeholder="Enter reference code"
                />
                {errors.your_ref && <p className="text-red-500 text-sm">{errors.your_ref}</p>}
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Driver Info */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Driver</label>
                <input
                  type="text"
                  value={loadingCarDetails ? 'Loading...' : selectedDriver}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 cursor-not-allowed"
                  disabled
                />
              </div>

              {/* Actual Balance */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Actual Balance</label>
                <input
                  type="text"
                  value={
                    loadingCarDetails
                      ? 'Loading...'
                      : selectedActualBalance
                        ? new Intl.NumberFormat('en-KE', {
                          style: 'currency',
                          currency: 'KES',
                        }).format(selectedActualBalance)
                        : 'KES 0.00'
                  }
                  className={`w-full px-4 py-3 border border-gray-300 rounded-xl cursor-not-allowed text-right font-mono ${loadingCarDetails
                    ? 'bg-gray-100'
                    : selectedActualBalance && parseFloat(selectedActualBalance) > 3000
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-50'
                    }`}
                  disabled
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Transaction Notes</label>
                <textarea
                  value={newTrans.notes}
                  onChange={(e) => setNewTrans({ ...newTrans, notes: e.target.value })}
                  onPaste={handleNotesPaste}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white resize-none h-32"
                  placeholder="Enter transaction notes (paste M-Pesa message to auto-fill reference, amount, and date)"
                />
                {errors.notes && <p className="text-red-500 text-sm">{errors.notes}</p>}
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#2a2e30]">Amount (KES)</label>
                <input
                  type="number"
                  value={newTrans.amount}
                  onChange={(e) => setNewTrans({ ...newTrans, amount: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white text-right font-mono"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                {errors.amount && <p className="text-red-500 text-sm">{errors.amount}</p>}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              className="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 flex items-center"
              onClick={() => {
                setNewTrans({
                  car_trans_type: '',
                  date: new Date().toISOString().split('T')[0],
                  amount: '',
                  notes: '',
                  car: '',
                  bal: '',
                  input_by: user.username,
                  your_ref: ''
                });
                setSelectedDriver('');
                // removed outdated balance state
                setSelectedActualBalance('');
                setErrors({});
              }}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>

            <button
              className={`px-8 py-3 font-semibold rounded-xl transition-all duration-200 flex items-center ${isSaveDisabled || isSaving
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#FF5733] text-white hover:bg-[#e64a2b] shadow-lg hover:shadow-xl'
                }`}
              onClick={handleAddTransaction}
              disabled={isSaveDisabled || isSaving}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Post Transaction
                </>
              )}
            </button>
          </div>
        </div>

        {/* Last Transaction Details */}
        {lastTransaction && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-[#2a2e30] mb-6">Last Transaction Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-600">Date</label>
                <div className="px-3 py-2 bg-gray-50 rounded-xl border">
                  <span className="text-[#2a2e30] font-medium text-sm">
                    {lastTransaction.date ? new Date(lastTransaction.date).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-600">Document No</label>
                <div className="px-3 py-2 bg-gray-50 rounded-xl border">
                  <span className="text-[#2a2e30] font-medium text-sm">
                    {lastTransaction.doc_no || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-600">Transaction Type</label>
                <div className="px-3 py-2 bg-gray-50 rounded-xl border">
                  <span className="text-[#2a2e30] font-medium text-sm">
                    {lastTransaction.type || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-600">Reference</label>
                <div className="px-3 py-2 bg-gray-50 rounded-xl border">
                  <span className="text-[#2a2e30] font-medium text-sm">
                    {lastTransaction.your_ref || 'N/A'}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-600">Input By</label>
                <div className="px-3 py-2 bg-gray-50 rounded-xl border">
                  <span className="text-[#2a2e30] font-medium text-sm">
                    {lastTransaction.by || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Transaction;
